// Background sync loop. Runs continuously:
//   - flushes pending redemptions whenever we're online
//   - tops up the local ticket pool when it dips below MIN_TICKETS
//
// Designed to fail silently — losing connection during a sync attempt should
// just retry on the next tick, never throw at the UI.

import { api, ApiError } from './api';
import {
  storeTickets,
  pendingRedemptions,
  clearRedeemed,
  ticketCount,
  type OfflineTicket,
} from './offlineDb';

const TOPUP_THRESHOLD = 5; // when ticket count drops below this, fetch more
const TOPUP_BATCH = 20; // how many to fetch each top-up
const TICK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes background sync
// Reentrancy guard so a manual Sync Now won't double-run if a tick is already
// in flight or two button presses arrive back-to-back.
let syncInFlight = false;

let tickHandle: number | null = null;
let onlineNow = typeof navigator !== 'undefined' ? navigator.onLine : true;

type Listener = (state: { online: boolean; ticketsRemaining: number; redemptionsPending: number }) => void;
const listeners = new Set<Listener>();

export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

async function emit() {
  const [t, r] = await Promise.all([ticketCount(), pendingRedemptions().then((rs) => rs.length)]);
  const snapshot = { online: onlineNow, ticketsRemaining: t, redemptionsPending: r };
  for (const fn of listeners) fn(snapshot);
}

async function flushRedemptions() {
  const pending = await pendingRedemptions();
  if (!pending.length) return;
  try {
    const res = await api.redeem(pending);
    if (res.accepted?.length) await clearRedeemed(res.accepted);
    // Also clear rejections that aren't going to succeed on retry. These are
    // bookkeeping failures (already-redeemed, malformed) — the matching ticket
    // is gone server-side, no point keeping the client redemption around.
    const unrecoverable = (res.rejected ?? [])
      .filter((r) => r.reason === 'not_found' || r.reason === 'wrong_device' || r.reason === 'invalid_fields')
      .map((r) => r.ticket_uuid);
    if (unrecoverable.length) await clearRedeemed(unrecoverable);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) throw e; // bubble auth errors
    // network error: leave them queued, try again next tick.
  }
}

async function topUpTickets() {
  const count = await ticketCount();
  if (count >= TOPUP_THRESHOLD) return;
  try {
    const res = await api.tickets(TOPUP_BATCH);
    const tickets: OfflineTicket[] = res.tickets.map((t) => ({
      ticket_uuid: t.ticket_uuid,
      won: t.won ? 1 : 0,
      prize: t.prize,
      expires_at: t.expires_at,
    }));
    await storeTickets(tickets);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) throw e;
    // ignore — try next tick.
  }
}

async function tick() {
  if (syncInFlight) return;
  syncInFlight = true;
  try {
    if (!onlineNow) return;
    await flushRedemptions();
    await topUpTickets();
  } finally {
    syncInFlight = false;
    await emit();
  }
}

// Manual trigger from the History panel's Sync Now button. Returns a tiny
// summary the UI can show as feedback ("Synced 3 spins" / "Already up to date").
export async function syncNow(): Promise<{ synced: number; ticketsFetched: number; online: boolean }> {
  if (!onlineNow) return { synced: 0, ticketsFetched: 0, online: false };
  if (syncInFlight) {
    // Wait for the in-flight tick to finish before reporting.
    while (syncInFlight) await new Promise((r) => setTimeout(r, 50));
  }
  const { pendingRedemptions, ticketCount } = await import('./offlineDb');
  const before = await pendingRedemptions().then((rs) => rs.length);
  const ticketsBefore = await ticketCount();
  await tick();
  const after = await pendingRedemptions().then((rs) => rs.length);
  const ticketsAfter = await ticketCount();
  return {
    synced: Math.max(0, before - after),
    ticketsFetched: Math.max(0, ticketsAfter - ticketsBefore),
    online: true,
  };
}

export function startOfflineSync() {
  if (tickHandle !== null) return;
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      onlineNow = true;
      void tick();
    });
    window.addEventListener('offline', () => {
      onlineNow = false;
      void emit();
    });
  }
  void tick(); // first run immediately
  tickHandle = window.setInterval(tick, TICK_INTERVAL_MS);
}

export function stopOfflineSync() {
  if (tickHandle !== null) {
    clearInterval(tickHandle);
    tickHandle = null;
  }
}

export function isOnline() {
  return onlineNow;
}
